import { useEffect } from 'react';
import { useT } from '../i18n/useLanguage';

interface JoinModalProps {
  open: boolean;
  onClose: () => void;
}

// "อ่านเพิ่มเติม" modal — Figma 120:1164. Brown panel with the same
// diamond-tile pattern used elsewhere in the page, a yellow pill title
// flanked by the fork-spoon icon, a scrollable Thai body and a
// decorative left-edge scroll track with a cat-paw thumb. Triggered by
// the CTA "Join" button in CampaignSection.
export default function JoinModal({ open, onClose }: JoinModalProps) {
  const t = useT();

  // Close on ESC + lock body scroll while the modal is open so the
  // page's snap-scroll doesn't fight touch scrolling inside the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('cta.readMore')}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4 py-6"
    >
      {/* Panel — Figma 120:1165 (617×873). Aspect-locked so the title
          + scroll-track composition reads consistently at any size;
          max-h clamp keeps short viewports from blowing past the
          screen. Diamond-tile pattern matches Story / Campaign so the
          modal feels part of the same world. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative flex w-full max-w-[617px] flex-col overflow-hidden rounded-[20px] border-[3px] border-white bg-section-feature shadow-[0_13px_0_0_rgba(0,0,0,0.25)]"
        style={{
          backgroundImage: "url('/images/diamond-tile.svg')",
          backgroundRepeat: 'repeat',
          backgroundSize: '64px 64px',
          aspectRatio: '617 / 873',
          maxHeight: 'min(90vh, 873px)',
        }}
      >
        {/* Top fade (Figma 120:1168) — softens the diamond pattern
            behind the title row. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[12%] bg-gradient-to-b from-section-feature to-transparent" />

        {/* Bottom fade — mirror of the top fade. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[12%] bg-gradient-to-t from-section-feature to-transparent" />

        {/* Close button (Figma 120:1169). Uses the pre-rendered
            icon-close.png so the cross matches the rest of the cooking
            iconography without per-pixel CSS. */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-30 transition hover:scale-110 active:scale-95"
        >
          <img
            src="/images/icon-close.png"
            alt=""
            aria-hidden="true"
            className="block h-10 w-10 select-none md:h-12 md:w-12"
            draggable={false}
          />
        </button>

        {/* Decorative left scroll track (Figma 120:1185 + 120:1186) —
            aesthetic only; actual scrolling happens in the body
            below. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-[8%] left-[6%] top-[8%] z-10 w-2.5 rounded-pill bg-white/10"
        >
          <img
            src="/images/cat_paw.png"
            alt=""
            aria-hidden="true"
            className="absolute -left-2.5 top-[10%] h-7 w-auto -translate-y-1/2 select-none"
            draggable={false}
          />
        </div>

        {/* Title row — pre-rendered campaign-modal-header.png with the
            fork-spoon icon, yellow pill, "อ่านเพิ่มเติม" label and
            accent dot all baked in. Width is a % of the panel so the
            header scales with the rest of the modal; `h-auto` keeps
            the asset's native aspect. */}
        <div className="relative z-20 flex shrink-0 items-center justify-center px-12 pb-2 pt-12">
          <img
            src="/images/campaign-modal-header.png"
            alt={t('cta.readMore')}
            className="block h-auto w-[70%] select-none md:w-[60%]"
            draggable={false}
          />
        </div>

        {/* Scrollable body — campaign detail paragraphs. The fades
            above/below mask the top/bottom edge as the user scrolls.
            `pb-2` is tight because the apply CTA footer below carries
            the remaining bottom spacing. */}
        <div className="relative z-20 flex-1 overflow-y-auto overscroll-contain px-12 pb-2 pt-4">
          <div className="whitespace-pre-wrap text-center font-thai text-base font-bold leading-[1.6] text-white md:text-lg">
            {t('campaign.modal.body')}
          </div>
        </div>

        {/* Apply CTA footer — pinned below the scroll area (not part of
            the scrolling body) so the registration link is always
            visible. `z-30` keeps it above the bottom fade overlay, and
            `stopPropagation` prevents the backdrop close handler from
            firing when the user taps the link. Opens the Google Form in
            a new tab. */}
        <div className="relative z-30 shrink-0 px-12 pb-10 pt-2">
          <a
            href={t('campaign.modal.applyUrl')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="mx-auto block w-fit rounded-pill border-2 border-white bg-[#FFC0D1] px-8 py-3 text-center font-display text-base font-extrabold text-section-feature shadow-[0_4px_0_0_rgba(0,0,0,0.25)] transition hover:scale-105 active:scale-95 md:text-lg"
          >
            {t('campaign.modal.applyLabel')}
          </a>
        </div>
      </div>
    </div>
  );
}
